/**
 * URL query → 层级恢复参数
 *
 * 输出 parsed 结构（与 session 恢复、applyHierarchyFromParsed 共用）：
 * {
 *   category: { type, id, name, origin, num? },   // curCategory 源数据
 *   singleCapFlag: boolean,                        // 是否单 campaign 汇总层
 *   breadcrumbs: Array                             // SearchPanel 面包屑路径
 * }
 */
import { QUERY_KEYS } from './constants';
import { buildBreadcrumbsFromState, MULTI_CAMPAIGN_BREADCRUMB_NAME } from './breadcrumbs';
import {
    getQueryAccountId,
    getQueryCampaignId,
    hasAccountAccess,
    isMultiCampaignSelection,
    resolveAccountIds,
    resolveCampaignIds
} from './normalizeQuery';

/**
 * 判定 URL query 的层级状态是否可恢复
 *
 * 返回四种状态：
 * - ok：层级参数完整且账户有权限，可直接恢复
 * - mismatch：参数非法或缺失（type 无效 / ac_id 缺失 / ad 缺 c_id / 残留孤立 ID），应回首页
 * - noAccess：账户不在权限列表中，应回首页并提示
 * - pending：accountList 尚未加载，暂无法判定，需延迟重试
 *
 * @param {Object} query        vue-router $route.query
 * @param {Array|null} accountList  账户下拉列表（null = 未加载）
 * @returns {'ok'|'mismatch'|'noAccess'|'pending'}
 */
export function resolveQueryHierarchyStatus(query = {}, accountList = []) {
    const type = query[QUERY_KEYS.TYPE] || 'account';

    // account 层级：不应残留 ac_id / c_id（可能是被手动删减的深层级 URL）
    if (type === 'account') {
        return (getQueryAccountId(query) || getQueryCampaignId(query)) ? 'mismatch' : 'ok';
    }
    // type 只允许 account / campaign / ad
    if (type !== 'campaign' && type !== 'ad') return 'mismatch';
    // campaign / ad 层级必须有 ac_id
    if (!getQueryAccountId(query)) return 'mismatch';
    // ad 层级必须有 c_id
    if (type === 'ad' && !getQueryCampaignId(query)) return 'mismatch';

    // 账户权限三态判定
    const access = hasAccountAccess(accountList, getQueryAccountId(query));
    if (access === null) return 'pending';
    return access ? 'ok' : 'noAccess';
}

/**
 * 在 accountList 中查找 ac_id 对应的下拉选项（含 label 显示名）
 * @returns {Object|null} { value, label } 或 null
 */
function findAccountOption(accountList, accountIdStr) {
    const [primaryId] = resolveAccountIds(accountIdStr);
    if (!primaryId || !accountList?.length) return null;
    return accountList.find(option => String(option.value) === primaryId) || null;
}

/**
 * 从 URL query 解析层级恢复参数
 *
 * 解析逻辑：
 * 1. account 层级 → 直接返回全部账户 parsed
 * 2. campaign / ad 层级 → 校验 accountId 权限 → 构建 category + breadcrumbs
 * 3. campaign 层级的 singleCapFlag 由 c_id 是否存在推断：
 *    有 c_id → 多 campaign 中间层（false），无 c_id → 单 campaign 汇总（true）
 *
 * @param {Object} query       vue-router $route.query
 * @param {Array}  accountList 账户下拉列表
 * @returns {{ category, singleCapFlag, breadcrumbs }|null} null = 参数无效或 accountList 未就绪
 */
export function parseDashboardQuery(query = {}, accountList = []) {
    const type = query[QUERY_KEYS.TYPE] || 'account';

    // account 层级无需账户信息
    if (type === 'account') {
        return {
            category: { type: 'account', origin: {}, name: 'msn.dashboard.allAccounts' },
            singleCapFlag: true,
            breadcrumbs: []
        };
    }

    // campaign / ad 层级：校验账户权限
    const accountId = getQueryAccountId(query);
    if (!accountId || hasAccountAccess(accountList, accountId) !== true) return null;

    const accountOption = findAccountOption(accountList, accountId);
    if (!accountOption) return null;

    const accountName = accountOption.label;
    const campaignId = getQueryCampaignId(query);
    const isMultiCampaign = isMultiCampaignSelection(campaignId);
    const displayName = isMultiCampaign ? MULTI_CAMPAIGN_BREADCRUMB_NAME : campaignId;
    // campaign 层级有 c_id → 多 campaign 中间层，无 c_id → 单 campaign 汇总
    const singleCapFlag = type !== 'campaign' || !campaignId;

    // 公共面包屑构建参数
    const crumbParams = { type, accountId, accountName, campaignId, campaignName: displayName, singleCapFlag };

    if (type === 'campaign') {
        return {
            category: {
                type: 'campaign',
                id: accountId,
                name: accountName,
                origin: singleCapFlag ? { accountId } : { accountId, campaignId }
            },
            singleCapFlag,
            breadcrumbs: buildBreadcrumbsFromState(crumbParams)
        };
    }

    // ad 层级必须有 c_id
    if (type === 'ad' && campaignId) {
        const category = {
            type: 'ad',
            id: campaignId,
            name: displayName,
            origin: { accountId, campaignId }
        };
        if (isMultiCampaign) category.num = resolveCampaignIds(campaignId).length;

        return {
            category,
            singleCapFlag: true,
            breadcrumbs: buildBreadcrumbsFromState({ ...crumbParams, singleCapFlag: true })
        };
    }

    return null;
}
